import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * How far a one-row toolbar has folded to fit, from 0 (roomiest) to `last`, found before paint: each
 * layout pass that finds the row's last child past the row's end takes the next fold. A new width, or
 * new content (`content`, any string that changes with what the row holds), starts again from 0, so
 * a wider pane or a shorter label gets its words and keycaps back. The last child's edge is measured
 * rather than the row's scroll width, which an open menu inside it would widen. Measured, not set by
 * breakpoints, because what such a row holds changes with the data (DESIGN.md, the conversation
 * head's toolbar; the selection bar). What each fold hides is the caller's.
 */
export function useFold(bar: RefObject<HTMLElement | null>, content: string, last: number): number {
  const [fold, setFold] = useState(0);
  // Bumped with each new width, so the row is measured again even when its fold was already 0 and
  // nothing else would render it: a late font swap or a scrollbar appearing narrows it outside React.
  const [, setWidthSeen] = useState(0);
  const seen = useRef(content);
  const width = useRef<number | null>(null);
  useEffect(() => {
    const element = bar.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      const before = width.current;
      width.current = next;
      if (before === null || before === next) return;
      setFold(0);
      setWidthSeen((count) => count + 1);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [bar]);
  useLayoutEffect(() => {
    if (seen.current !== content) {
      seen.current = content;
      // Already at 0 nothing would render again, so the new content is measured in this pass.
      if (fold !== 0) {
        setFold(0);
        return;
      }
    }
    const element = bar.current;
    const end = element?.lastElementChild;
    if (!element || !end || fold >= last) return;
    if (end.getBoundingClientRect().right > element.getBoundingClientRect().right + 1) {
      setFold(fold + 1);
    }
  });
  return fold;
}
